/* eslint-disable no-magic-numbers */
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import {
  getWorkspaceById,
  isUUID,
  matchConsolidationsRoutePath,
  matchOnboardingSFARoute,
  QUERY_PARAMS,
  workspacesSelector
} from '@futrli/shared-services';
import { StoreState } from '@futrli/organisations/core/types';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';

/**
 * Returns wether or not to show the global nav. The global nav shows if user is a
 * Sage for Accountants (SFA) user.
 */
export const useGlobalNavStatus = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const workspaceIdRouteParam = useMemo(() => {
    const match = /\/workspaces\/([^/]+)/.exec(location?.pathname);
    return match && isUUID(match[1]) ? match[1] : null;
  }, [location?.pathname]);
  const organisationIdRouteParam = useMemo(() => {
    const match = /\/organisations\/([^/]+)/.exec(location?.pathname);
    return match && isUUID(match[1]) ? match[1] : null;
  }, [location?.pathname]);

  const urlParams = new URLSearchParams(window.location.search);

  const workspaceId = workspaceIdRouteParam || urlParams.get(QUERY_PARAMS.WORKSPACE_ID);
  const organisationId = organisationIdRouteParam || urlParams.get(QUERY_PARAMS.ORG_ID);

  const workspaces = useSelector((store: StoreState) => {
    const { normalised } = store;
    return workspacesSelector(normalised) as WorkspacesListingItem[];
  });
  const workspace = (workspaces || []).find(w => w.id === workspaceId);

  const onlyExternalOrgs = useMemo(() => {
    if (workspace) {
      return workspace.organisations.length > 0 && workspace.organisations.every(org => org.is_externally_managed);
    }
    return false;
  }, [workspace]);

  const onlySfAOrgs = useMemo(() => {
    if (workspace) {
      return (
        workspace.organisations.length > 0 &&
        workspace.organisations.every(org => org.kind === 'sage_practice' || org.kind === 'sage_non_practice')
      );
    }
    return false;
  }, [workspace]);

  const displayGlobalNav = useMemo(() => {
    if (matchOnboardingSFARoute(location.pathname)) {
      return true;
    }

    if (matchConsolidationsRoutePath(location.pathname)) {
      return false;
    }

    if (workspace) {
      if (organisationId) {
        return !!workspace.organisations.find(org => org.id === organisationId)?.display_global_nav;
      } else {
        return workspace.organisations.length > 0 && workspace.organisations.every(org => org.display_global_nav);
      }
    }

    return false;
  }, [location.pathname, organisationId, workspace]);

  useEffect(() => {
    if (workspaceId && !workspace) {
      dispatch(getWorkspaceById(workspaceId));
      return;
    }
  }, [dispatch, workspace, workspaceId]);

  return { displayGlobalNav, onlyExternalOrgs, onlySfAOrgs };
};
